'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import * as FC from './formcomponents';
import * as FVF from './formfieldvalidations';
import { capitalize, attributeMap } from '../../foundation/utilities';
import { FormEngine } from './formengine';
const debug = false;
/*
===== FORM EXTRACT =====
Submit a page URL of a form to extract all form elements and
convert them to JSON to create a form via FormEngine
*/
/**
 * FormExtractor — Extract a form from a URL or pasted HTML and convert it into a JSON schema usable by `FormEngine`.
 *
 * @param {string} [props.url] - URL pointing to a page containing a form to extract.
 * @param {string} [props.htmlPaste] - Raw HTML snippet to parse for form elements instead of fetching a URL.
 */
FormExtractor.propTypes = {
    /** URL of the page to extract form elements from */
    url: PropTypes.string,
    /** Raw HTML string containing a form to extract */
    htmlPaste: PropTypes.string
};
export function FormExtractor(props) {
    const [url, setURL] = useState(props.url || "");
    const [htmlPaste, setHtmlPaste] = useState(props.htmlPaste || "");
    const [formData, setFormData] = useState({ fields: [] });
    /**
     * setParentState — Callback invoked by child UI to pass URL or pasted HTML values up to the parent extractor.
     *
     * @param {string} [props.url] - URL value provided by the UI.
 * @param {string} [props.htmlPaste] - Pasted HTML value provided by the UI.
     */
    setParentState.propTypes = {
        /** URL value provided by the child UI */
        url: PropTypes.string.isRequired,
        /** Pasted HTML provided by the child UI */
        htmlPaste: PropTypes.string.isRequired,
    };
    function setParentState(childState) {
        // SET STATE FROM PARENT VALUES - EXPOSED EXTERNAL
        if (debug)
            console.log("Setting Parent State...");
        setURL(childState.url);
        setHtmlPaste(childState.htmlPaste);
    }
    return (_jsxs("div", { children: [_jsx("div", { className: "section-container", children: _jsx(FormExtractUI, { setParentState: setParentState }) }), _jsxs("div", { className: "section-container", children: [_jsx("br", {}), _jsx("br", {}), _jsx("hr", {}), _jsx("br", {}), _jsx("br", {})] }), _jsx("div", { className: "section-container", children: _jsx(FormExtractEngine, { url: url, htmlPaste: htmlPaste, setFormData: setFormData }) }), _jsxs("div", { className: "section-container", children: [_jsx("br", {}), _jsx("br", {}), _jsx("hr", {}), _jsx("br", {}), _jsx("br", {})] }), _jsx("div", { className: "section-container", children: _jsx(FormEngine, { formData: formData }) })] }));
}
/**
 * FormExtractUI — UI allowing the user to enter a URL or paste HTML to extract form fields.
 *
 * @param {function} [props.setParentState] - Callback called with `{ url, htmlPaste }` when the user submits input.
 */
FormExtractUI.propTypes = {
    /** Callback to send URL/html values back to parent */
    setParentState: PropTypes.func.isRequired
};
export function FormExtractUI(props) {
    const [url, setURL] = useState();
    const [htmlPaste, setHtmlPaste] = useState();
    function onChange(event) {
        // UPDATE URL OR HTML_PASTE ON CHANGE - EXTERNAL
        if (debug)
            console.log("Element Changed...");
        const target = event.target;
        if (target.name == "url") {
            setURL(target.value);
        }
        else if (target.name == "htmlPaste") {
            setHtmlPaste(target.value);
        }
    }
    function onSubmit() {
        // SET PARENT STATE WITH URL OR HTML PASTE - EXTERNAL
        if (debug)
            console.log("Form Submitted...");
        props.setParentState({ url: url, htmlPaste: htmlPaste });
    }
    return (_jsxs("form", { onSubmit: e => { e.preventDefault(); }, method: "post", name: "extract", children: [_jsx("label", { htmlFor: "url", children: "URL : " }), _jsx("input", { type: "text", list: "form_urls", id: "url", name: "url", size: 100, onChange: onChange }), _jsx(FC.FormDataList, { id: 'form_urls', items: FVF.formURLs }), _jsx("div", { style: { width: '100%', textAlign: 'center' }, children: "OR" }), _jsx("label", { htmlFor: "htmlPaste", children: "HTML : " }), _jsx("textarea", { id: "htmlPaste", name: "htmlPaste", rows: 10, cols: 80, onChange: onChange }), _jsx("br", {}), _jsxs("div", { style: { width: '100%', textAlign: 'center' }, children: [_jsx("button", { type: "button", onClick: onSubmit, children: "Extract" }), _jsx("input", { type: "reset" })] })] }));
}
/**
 * FormExtractEngine — Parse the supplied URL or pasted HTML and convert found form elements into a JSON schema.
 *
 * @param {string} [props.url] - URL to fetch and parse for form markup.
 * @param {string} [props.htmlPaste] - Raw HTML to parse instead of fetching a URL.
 * @param {function} [props.setFormData] - Callback which receives the generated form JSON schema.
 */
FormExtractEngine.propTypes = {
    /** URL to fetch and parse */
    url: PropTypes.string,
    /** Raw HTML string to parse for forms */
    htmlPaste: PropTypes.string,
    /** Callback to receive the generated form JSON */
    setFormData: PropTypes.func.isRequired
};
export function FormExtractEngine(props) {
    const proxy = 'https://proxy.pixelated.tech/prod/proxy?url=';
    // const proxy = "https://thingproxy.freeboard.io/fetch/";
    const [url, setURL] = useState(props.url);
    const [htmlPaste, setHtmlPaste] = useState(props.htmlPaste);
    const [formJson, setFormJson] = useState();
    function getHTML(url, callback) {
        // GET SERVER SIDE HTML THROUGH XMLHTTPREQUEST - INTERNAL
        if (debug)
            console.log("Getting HTML From URL...");
        const xhr = new XMLHttpRequest();
        // xhr.onreadystatechange = (e) => {
        xhr.onreadystatechange = () => {
            if (xhr.readyState === 4 && xhr.status === 200) {
                const json = callback(xhr.responseXML);
                setFormJson(json);
                props.setFormData(json);
            }
        };
        xhr.open('GET', url);
        xhr.responseType = 'document';
        xhr.send(null);
    }
    useEffect(() => {
        if (debug)
            console.log("Component Is Mounted...");
        if (props.url) {
            const newURL = proxy + props.url;
            if (!url || (props.url !== url)) {
                setURL(props.url);
                getHTML(newURL, formToJSON);
            }
            else if (url && !formJson) {
                getHTML(newURL, formToJSON);
            }
        }
        else if (props.htmlPaste) {
            let json;
            if (!htmlPaste || (props.htmlPaste !== htmlPaste)) {
                setHtmlPaste(props.htmlPaste);
                const thisHTML = new DOMParser().parseFromString(props.htmlPaste, 'text/html');
                json = formToJSON(thisHTML);
                setFormJson(json);
                props.setFormData(json);
            }
            else if (htmlPaste && !formJson) {
                const thisHTML = new DOMParser().parseFromString(htmlPaste, 'text/html');
                json = formToJSON(thisHTML);
                setFormJson(json);
                props.setFormData(json);
            }
        }
    }); // no dependencies = run on every render
    return (_jsx("div", { children: _jsx("pre", { id: 'formJson', children: JSON.stringify(formJson, null, 2) }) }));
}
export function extractOptions(thisElement) {
    // GENERATE OPTIONS FOR SELECT FIELD - INTERNAL
    if (debug)
        console.log("Extracting Options...");
    if (thisElement.tagName.toLowerCase() === 'select' && thisElement.options && thisElement.options.length) {
        const options = [];
        for (let option = 0; option < thisElement.options.length; option++) {
            const thisOption = thisElement.options[option];
            const attribs = {};
            for (let attrib = 0; attrib < thisOption.attributes.length; attrib++) {
                const thisAttrib = thisOption.attributes[attrib];
                attribs[attributeMap(thisAttrib.name)] = thisAttrib.value;
            }
            if (thisOption.innerText)
                attribs.text = thisOption.innerText;
            if (Object.keys(attribs).length > 0)
                options.push(attribs); // not empty then add
        }
        if (options.length > 0)
            return options;
    }
    return false;
}
export function extractSelectedOptions(thisOptions) {
    if (debug)
        console.log("Extracting Selected Options...");
    // GENERATE LIST OF SELECTED OPTIONS FOR SELECT FIELD - INTERNAL
    if (thisOptions && thisOptions.length) {
        const selected = [];
        for (let option = 0; option < thisOptions.length; option++) {
            const thisOption = thisOptions[option];
            if (Object.prototype.hasOwnProperty.call(thisOption, 'selected')) {
                selected.push(thisOption.value);
                delete thisOptions[option].selected;
            }
        }
        // MULTIPLE - Return array
        if (selected.length > 1)
            return selected;
        // Not Multiple = return string
        if (selected.length === 1)
            return selected.toString();
    }
    return false;
}
export function extractAttribs(thisElement) {
    if (debug)
        console.log("Extracting Attributes...");
    // EXTRACT ALL ATTRIBUTES FOR A FORM ELEMENT - INTERNAL
    // LOOK AT THAT - JAVASCRIPT REFLECTION!
    if (thisElement && thisElement.attributes && thisElement.attributes.length) {
        const props = {};
        for (let attrib = 0; attrib < thisElement.attributes.length; attrib++) {
            const thisAttrib = thisElement.attributes[attrib];
            if (thisAttrib.name === 'style') {
                // Do not gather styles
            }
            else if (['disabled', 'multiple', 'readonly', 'required', 'selected'].indexOf(thisAttrib.name.toLowerCase()) > -1) {
                // Flag type Attributes
                props[attributeMap(thisAttrib.name)] = attributeMap(thisAttrib.name);
            }
            else if (thisAttrib && thisAttrib.name && thisAttrib.value) {
                // use attributeMap to map html props to React props
                props[attributeMap(thisAttrib.name)] = thisAttrib.value;
            }
        }
        if (Object.keys(props).length > 0)
            return props;
    }
    return false;
}
export function extractLabel(html, thisElement) {
    // IDENTIFY AND EXTRACT LABEL PROPERTIES FOR A SPECIFIC FIELD - INTERNAL
    if (debug)
        console.log("Extracting Label...");
    const myElement = thisElement.attributes.getNamedItem('id');
    if (myElement && myElement.value) {
        const thisID = myElement.value;
        const thisLabel = html.querySelector("label[for='" + thisID + "']");
        if (thisLabel)
            return thisLabel;
    }
    return false;
}
export function formToJSON(html) {
    // CONVERT HTML TO CONFIG JSON - INTERNAL
    if (debug)
        console.log("Converting Form to JSON...");
    const json = {};
    json.fields = [];
    const forms = html.getElementsByTagName('form');
    // ----- FORMS
    for (let form = 0; form < forms.length; form++) {
        const thisForm = forms[form];
        if (Object.keys(thisForm).length > 0) {
            // ----- ELEMENTS
            for (let element = 0; element < thisForm.elements.length; element++) {
                const thisElement = thisForm[element];
                const elem = {};
                if (thisElement && thisElement.attributes && thisElement.tagName) {
                    // ----- COMPONENT
                    elem.component = 'Form' + capitalize(thisElement.tagName);
                    let props = {};
                    // ----- ELEMENT ATTRIBUTES
                    const thisProps = extractAttribs(thisElement);
                    if (thisProps)
                        props = thisProps;
                    // ----- ELEMENT OPTIONS
                    const thisOptions = extractOptions(thisElement);
                    if (thisOptions) {
                        // only true for SELECT elements
                        const thisSelected = extractSelectedOptions(thisOptions);
                        if (thisSelected)
                            props.defaultValue = thisSelected;
                        props.options = thisOptions;
                    }
                    else {
                        // get innerText on all but SELECT elements
                        if (thisElement.innerText)
                            props.text = thisElement.innerText;
                    }
                    elem.props = props;
                    // ----- LABEL
                    const thisLabel = extractLabel(html, thisElement);
                    if (thisLabel) {
                        elem.props.label = (thisLabel.innerText || thisLabel.textContent);
                    }
                }
                if (Object.keys(elem).length > 0)
                    json.fields.push(elem);
            }
        }
    }
    return json;
}
